import React from 'react'
import { motion } from 'framer-motion'
import { ServiceType } from '../../types'
import { ErrorFallbackProps } from './ErrorBoundary'
import { getServiceConfig } from '../../data/services'
import { useAccessibility } from '../../hooks/useAccessibility'

export const ServiceErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  serviceType,
  resetError
}) => {
  const { announce } = useAccessibility()
  const serviceConfig = serviceType ? getServiceConfig(serviceType) : null

  React.useEffect(() => {
    announce('An error occurred. Please try again or contact support.', 'assertive')
  }, [announce])

  const handleRetry = () => {
    announce('Retrying...')
    resetError()
  }

  const handleGoHome = () => {
    announce('Navigating to home page')
    window.location.href = '/'
  }

  const handleContactSupport = () => {
    announce('Opening support contact')
    window.location.href = 'mailto:support@mernaki.de?subject=Error Report'
  }

  const getServiceSpecificMessage = () => {
    if (!serviceType || !serviceConfig) {
      return {
        title: 'Something went wrong',
        description: 'We encountered an unexpected error. Please try again or contact our support team.',
        suggestions: [
          'Refresh the page',
          'Check your internet connection',
          'Try again in a few minutes',
          'Contact support if the problem persists'
        ]
      }
    }

    const serviceMessages: Partial<Record<ServiceType, { title: string; description: string; suggestions: string[] }>> = {
      [ServiceType.HR]: {
        title: 'HR Service Temporarily Unavailable',
        description: 'We\'re having trouble loading the HR template service. Your data is safe.',
        suggestions: [
          'Try refreshing the page',
          'Check if you have any pending downloads',
          'Contact support for urgent template needs',
          'Visit our status page for updates'
        ]
      },
      [ServiceType.COMPLIANCE]: {
        title: 'Compliance Service Error',
        description: 'The compliance service is currently experiencing issues. We\'re working to resolve this.',
        suggestions: [
          'Try again in a few minutes',
          'Check our service status page',
          'Contact support for urgent compliance matters',
          'Subscribe to updates for this service'
        ]
      },
      [ServiceType.RARE_EARTH_PERMIT]: {
        title: 'Rare Earth Permit Service Error',
        description: 'The rare earth permit service is temporarily unavailable. We\'re working to resolve this.',
        suggestions: [
          'Try again in a few minutes',
          'Check our service status page',
          'Contact support for urgent permit needs',
          'Subscribe to updates for this service'
        ]
      },
      [ServiceType.TRADE]: {
        title: 'Trade Service Error',
        description: 'We\'re having trouble connecting to the trade permit service. Please try again.',
        suggestions: [
          'Verify your internet connection',
          'Try refreshing the page',
          'Contact support for urgent permit needs',
          'Check our service announcements'
        ]
      },
      [ServiceType.INTELLIGENCE]: {
        title: 'Intelligence Service Error',
        description: 'The market intelligence service is temporarily unavailable. We\'re working on it.',
        suggestions: [
          'Try again in a few minutes',
          'Check our system status',
          'Contact support for urgent data needs',
          'Subscribe to service updates'
        ]
      },
      [ServiceType.VERPACKG]: {
        title: 'VerpackG Service Error',
        description: 'The VerpackG-Löschung service is temporarily unavailable. We\'re working on it.',
        suggestions: [
          'Try again in a few minutes',
          'Check our service status page',
          'Contact support for urgent de-registration needs',
          'Subscribe to service updates'
        ]
      }
    }

    return serviceMessages[serviceType] || serviceMessages[ServiceType.HR]
  }

  const { title, description, suggestions } = getServiceSpecificMessage() || {
    title: 'Service Error',
    description: 'An unexpected error occurred with the service.',
    suggestions: ['Please try again later', 'Contact support if the issue persists']
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl border border-white/50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-label="Error icon"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{title}</h1>
                {serviceConfig && (
                  <p className="text-red-100 mt-1">
                    {serviceConfig.name} Service
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              {description}
            </p>

            {/* Error Details (Development only) */}
            {import.meta.env.DEV && error && (
              <details className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                  Technical Details (Development)
                </summary>
                <div className="text-sm text-gray-600 font-mono bg-white p-3 rounded border overflow-auto">
                  <p className="font-semibold mb-2">Error: {error.message}</p>
                  {error.stack && (
                    <pre className="whitespace-pre-wrap text-xs">
                      {error.stack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Suggestions */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                What you can do:
              </h2>
              <ul className="space-y-2" role="list">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-3" role="listitem">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={handleRetry}
                className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Try again"
              >
                <svg
                  className="w-5 h-5 mr-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Try Again
              </motion.button>

              <motion.button
                onClick={handleGoHome}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-h-[44px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Go to home page"
              >
                <svg
                  className="w-5 h-5 mr-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Go Home
              </motion.button>

              <motion.button
                onClick={handleContactSupport}
                className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 min-h-[44px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Contact support team"
              >
                <svg
                  className="w-5 h-5 mr-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Contact Support
              </motion.button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 text-center">
              If this problem persists, please contact our support team at{' '}
              <a
                href="mailto:support@mernaki.de"
                className="text-blue-600 hover:text-blue-700 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
              >
                support@mernaki.de
              </a>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}